import React, { useState } from 'react';
import {
  Button,
  Table,
  Space,
  Tooltip,
  Card,
  Row,
  Col,
  Statistic,
  Input,
  Tag,
  message,
  Modal,
  Descriptions,
  Alert,
  Popconfirm
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  SearchOutlined,
  ReloadOutlined,
  CrownOutlined,
  PoweroffOutlined,
  CheckCircleOutlined,
  ShopOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import ProductForm from '../components/Products/ProductForm';
import { formatCFA } from '../utils/formatters';
import { serviceProduits, Produit } from '../services/productsService';
import { getImageUrl } from '../services/uploadService';
import api from '../services/api';

interface StatItem {
  title: string;
  value: number;
  color: string;
}

const ProductsPage: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [productFormVisible, setProductFormVisible] = useState<boolean>(false);
  const [selectedProduct, setSelectedProduct] = useState<Produit | null>(null);
  const [formLoading, setFormLoading] = useState<boolean>(false);
  const [products, setProducts] = useState<Produit[]>([]);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [detailProduct, setDetailProduct] = useState<Produit | null>(null);
  const [offers, setOffers] = useState<{id: number; title: string}[]>([]);
  const [shopMaintenance, setShopMaintenance] = useState<boolean>(false);
  const [isMaintenanceLoading, setIsMaintenanceLoading] = useState<boolean>(false);

  // Fonction pour obtenir le nom de l'offre
  const getOfferName = (offerId: number | null): string => {
    if (!offerId) return '';
    const offer = offers.find(o => o.id === offerId);
    return offer ? offer.title : 'Offre inconnue';
  };

  // Charger les produits depuis l'API
  const fetchProducts = async (page = 1) => {
    setLoading(true);
    try {
      const res = await serviceProduits.listerProduits({ page });
      setProducts(res.data);
      if ((res as any).shop_maintenance !== undefined) {
        setShopMaintenance((res as any).shop_maintenance);
      } else {
        setShopMaintenance(res.data.length > 0 && res.data.every(p => !p.actif));
      }
    } catch (e) {
      console.error('Erreur API produits:', e);
      message.error('Erreur lors du chargement des produits');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleShopMaintenance = async (enableMaintenance: boolean) => {
    setIsMaintenanceLoading(true);
    try {
      const res = await serviceProduits.basculerStatutGlobalProduits(!enableMaintenance);
      message.success(res.message || (enableMaintenance ? 'Boutique désactivée (Mode maintenance)' : 'Boutique réactivée (En ligne)'));
      setShopMaintenance(enableMaintenance);
      fetchProducts(1);
    } catch (e: any) {
      message.error(e.message || 'Erreur lors de la modification du statut de la boutique');
    } finally {
      setIsMaintenanceLoading(false);
    }
  };

  React.useEffect(() => {
    fetchProducts(1);
    // Charger les offres
    api.get('/maintenance-offers').then(res => {
      setOffers(res.data.data || res.data || []);
    }).catch(err => console.error('Erreur chargement offres:', err));
  }, []);

  const handleNewProduct = () => {
    setSelectedProduct(null);
    setProductFormVisible(true);
  };

  const handleEditProduct = (product: Produit) => {
    setSelectedProduct(product);
    setProductFormVisible(true);
  };

  const handleDeleteProduct = async (productId: number) => {
    setLoading(true);
    try {
      await serviceProduits.supprimerProduit(productId);
      message.success('Produit supprimé avec succès');
      fetchProducts(1);
    } catch (e) {
      message.error('Erreur lors de la suppression du produit');
    } finally {
      setLoading(false);
    }
  };

  const handleFormSubmit = async (values: any) => {
    setFormLoading(true);
    try {
      if (selectedProduct) {
        await serviceProduits.mettreAJourProduit(selectedProduct.id, values);
        message.success('Produit modifié avec succès');
      } else {
        await serviceProduits.creerProduit(values);
        message.success('Produit créé avec succès');
      }
      setProductFormVisible(false);
      fetchProducts(1);
    } catch (e) {
      message.error('Erreur lors de l\'enregistrement du produit');
    } finally {
      setFormLoading(false);
    }
  };

  const categoryLabels: Record<number, string> = {
    1: 'Climatisation',
    2: 'Chauffage',
    3: 'Ventilation',
    4: 'Service',
    5: 'Filtration'
  };

  const [pagination] = useState({
    current: 1,
    pageSize: 10,
    total: products.length,
  });

  // Configuration des colonnes
  const columns: ColumnsType<Produit> = [
    {
      title: 'Image',
      dataIndex: 'images',
      key: 'image',
      width: 80,
      render: (images: string[]) =>
        images && images.length > 0 ? (
          <img
            src={getImageUrl(images[0])}
            alt="Produit"
            style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 4 }}
          />
        ) : (
          <div style={{ width: 48, height: 48, background: '#f0f0f0', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#bbb' }}>—</div>
        ),
    },
    {
      title: 'Nom du produit',
      dataIndex: 'nom',
      key: 'nom',
      render: (text: string, record: Produit) => (
        <div>
          <div style={{ fontWeight: 500 }}>{text}</div>
          {record.offer_id && (
            <Tag 
              icon={<CrownOutlined />} 
              color="gold" 
              style={{ marginTop: 4, fontSize: '11px' }}
            >
              Avec {getOfferName(record.offer_id)}
            </Tag>
          )}
        </div>
      ),
    },
    {
      title: 'Référence',
      dataIndex: 'reference',
      key: 'reference',
      render: (text: string) => (
        <code style={{ backgroundColor: '#f5f5f5', padding: '2px 6px', borderRadius: '4px' }}>{text}</code>
      ),
    },
    {
      title: 'Catégorie',
      dataIndex: 'categorie_id',
      key: 'categorie_id',
      render: (id: number) => <Tag color="blue">{categoryLabels[id] || 'N/A'}</Tag>,
    },
    {
      title: 'Prix',
      dataIndex: 'prix',
      key: 'prix',
      render: (prix: number) => formatCFA(prix),
    },
    {
      title: 'Stock',
      dataIndex: 'quantite_stock',
      key: 'quantite_stock',
      render: (stock: number) => (
        <Tag color={stock > 0 ? 'green' : 'red'}>{stock > 0 ? `${stock} unités` : 'Rupture'}</Tag>
      ),
    },
    {
      title: 'Statut',
      dataIndex: 'actif',
      key: 'actif',
      render: (actif: boolean) => (
        <Tag color={actif ? 'green' : 'red'}>{actif ? 'Actif' : 'Inactif'}</Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Voir les détails">
            <Button type="text" icon={<EyeOutlined />} size="small" onClick={() => { setDetailProduct(record); setDetailModalVisible(true); }} />
          </Tooltip>
          <Tooltip title="Modifier">
            <Button type="text" icon={<EditOutlined />} size="small" onClick={() => handleEditProduct(record)} />
          </Tooltip>
          <Tooltip title="Supprimer">
            <Button type="text" icon={<DeleteOutlined />} size="small" danger onClick={() => handleDeleteProduct(record.id)} />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // Statistiques
  const stats: StatItem[] = [
    { title: 'Total Produits', value: products.length, color: '#1890ff' },
    { title: 'En stock', value: products.filter(p => p.quantite_stock > 0).length, color: '#52c41a' },
    { title: 'Rupture de stock', value: products.filter(p => p.quantite_stock === 0).length, color: '#f5222d' },
  ];

  return (
    <div style={{ padding: '24px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* En-tête */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '24px',
        backgroundColor: '#fff',
        padding: '16px 24px',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 600 }}>Gestion des Produits</h1>
          <p style={{ margin: '4px 0 0 0', color: '#666' }}>
            Gérez votre catalogue de produits et équipements
          </p>
        </div>
        <Space>
          <Popconfirm
            title={shopMaintenance ? "Mettre la boutique en ligne ?" : "Désactiver tous les produits (Mode Maintenance) ?"}
            description={
              shopMaintenance 
                ? "Tous les produits seront réactivés et la boutique redeviendra accessible sur l'application mobile."
                : "Tous les produits en ligne seront désactivés. Un message professionnel 'Boutique en maintenance, réouverture bientôt' s'affichera sur l'application mobile."
            }
            onConfirm={() => handleToggleShopMaintenance(!shopMaintenance)}
            okText="Oui, confirmer"
            cancelText="Annuler"
            okButtonProps={{ danger: !shopMaintenance, loading: isMaintenanceLoading }}
          >
            <Button
              danger={!shopMaintenance}
              type={shopMaintenance ? "primary" : "default"}
              icon={shopMaintenance ? <CheckCircleOutlined /> : <PoweroffOutlined />}
              size="large"
              loading={isMaintenanceLoading}
              style={shopMaintenance ? { backgroundColor: '#52c41a', borderColor: '#52c41a', height: '40px' } : { height: '40px' }}
            >
              {shopMaintenance ? "Réactiver la boutique (En ligne)" : "Désactiver la boutique (Maintenance)"}
            </Button>
          </Popconfirm>
          <Button 
            icon={<ReloadOutlined />} 
            size="large"
            onClick={() => fetchProducts(1)}
          >
            Actualiser
          </Button>
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            size="large"
            style={{ height: '40px' }}
            onClick={handleNewProduct}
          >
            Nouveau Produit
          </Button>
        </Space>
      </div>

      {/* Alerte Mode Maintenance */}
      {shopMaintenance && (
        <Alert
          message="Boutique actuellement en Mode Maintenance"
          description="Tous les produits sont désactivés en ligne. Sur l'application mobile, les clients voient le message d'information professionnel 'Boutique en maintenance, réouverture bientôt'."
          type="warning"
          showIcon
          icon={<ShopOutlined style={{ fontSize: '24px' }} />}
          style={{ marginBottom: '24px', borderRadius: '8px', border: '1px solid #ffe58f', backgroundColor: '#fffbe6' }}
          action={
            <Popconfirm
              title="Mettre la boutique en ligne ?"
              onConfirm={() => handleToggleShopMaintenance(false)}
              okText="Activer"
              cancelText="Annuler"
            >
              <Button size="middle" type="primary" style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}>
                Réactiver la boutique
              </Button>
            </Popconfirm>
          }
        />
      )}

      {/* Statistiques */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        {stats.map((stat, index) => (
          <Col xs={24} sm={12} md={8} key={index} style={{ flex: 1 }}>
            <Card size="small" style={{ borderRadius: '8px', width: '100%' }}>
              <Statistic
                title={stat.title}
                value={stat.value}
                valueStyle={{ 
                  color: stat.color,
                  fontSize: '24px',
                  fontWeight: 600
                }}
              />
            </Card>
          </Col>
        ))}
      </Row>

      {/* Filtres */}
      <Card style={{ marginBottom: '16px', borderRadius: '8px' }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <Input
              placeholder="Rechercher par nom ou SKU..."
              prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
              allowClear
              size="large"
            />
          </Col>
        </Row>
      </Card>

      {/* Tableau */}
      <Card style={{ borderRadius: '8px' }}>
        <Table
          columns={columns}
          dataSource={products}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} sur ${total} produits`,
          }}
          scroll={{ x: 1000 }}
        />
      </Card>

      {/* Modal de formulaire produit */}
      <ProductForm
        visible={productFormVisible}
        onCancel={() => setProductFormVisible(false)}
        onSubmit={handleFormSubmit}
        initialValues={selectedProduct}
        loading={formLoading}
      />

      {/* Modal de détails produit */}
      <Modal
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={null}
        title={detailProduct?.nom || 'Détail produit'}
        width={600}
      >
        {detailProduct && (
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="Image">
              {detailProduct.images && detailProduct.images.length > 0 ? (
                <img
                  src={getImageUrl(detailProduct.images[0])}
                  alt="Produit"
                  style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 8 }}
                />
              ) : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Référence">{detailProduct.reference}</Descriptions.Item>
            <Descriptions.Item label="Description">{detailProduct.description}</Descriptions.Item>
            <Descriptions.Item label="Prix">{formatCFA(detailProduct.prix)}</Descriptions.Item>
            <Descriptions.Item label="Stock">{detailProduct.quantite_stock}</Descriptions.Item>
            <Descriptions.Item label="Catégorie">{categoryLabels[detailProduct.categorie_id] || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Statut">{detailProduct.actif ? 'Actif' : 'Inactif'}</Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default ProductsPage;